import { useRef, useState, type ChangeEvent } from 'react';
import { line, Color, EXEMPTION_REASONS, Size, US_REGIONS, US_STATE_NAMES, exemptionFormType, exemptionFormTypesFor, exemptionReasonLabel, exemptionReasonsFor, t, STREAMLINED_EXEMPTION_FORM, stateExemptionForm, type TaxFormsAnswer } from '@bms2/shared';
import { Button, Checkbox, DatePicker, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, del, patch, post, upload, useApi, useRows, FILES_PATH } from './api';
import { TAX_FORMS_PATH, TAX_FORMS_ROUTE, taxFormPath } from '../accounting/api';
import { useFetchedBlob } from '../../components/files/useFetchedBlob';
import modalStyles from '../../components/modals/modal.module.css';
import styles from './RecordPage.module.css';

/** The municipality choice that opens a typed name, for a city the system has no location in yet. */
const OTHER = '__other__';

interface SiteModalProps {
  open: boolean;
  siteId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

interface CertificateReading {
  reading: 'read' | 'unread';
  /** Why an unread document was not read, where the reader could say (decision 0678); absent from an older answer. */
  unreadReason?: 'noVisionKey' | null;
  /** How the page was read: its form fields, its text or its picture (decision 0347); absent from an older answer. */
  method?: 'fields' | 'text' | 'ocr' | null;
  nameFound: boolean | null;
  effectiveDate: string | null;
  expiryDate: string | null;
  stateCode?: string | null;
  exemptionReason?: string | null;
  formCode?: string | null;
  purchaserName?: string | null;
  isBlanket?: boolean | null;
}

type ReadState = 'idle' | 'reading' | 'found' | 'missing' | 'unread' | 'noVisionKey';

/** A live certificate row as the Site page's tile carries it, which Edit opens the modal on (decision 0676). */
export interface CertificateEdit {
  id: string;
  stateCode: string;
  exemptionReason: string;
  municipality: string | null;
  formType: string | null;
  effectiveFrom: string;
  expiresOn: string | null;
  fileId: string;
  fileName: string | null;
  /** Decision 0679: uploaded by a contact through a request link; its Save is the review. */
  awaitingReview?: boolean;
}

/** The reason code a stored reason reads back as: a listed reason by its label, anything else as Other in its own words. */
function reasonCodeOf(stored: string): { code: string; other: string } {
  const listed = EXEMPTION_REASONS.find((row) => row.label === stored || row.code === stored);
  return listed ? { code: listed.code, other: '' } : { code: 'Other', other: stored };
}

/**
 * A tax exempt certificate on the site (piece 10; decision 0274). 🔒 The file
 * is the first control and is sent as soon as it is picked; the intake seam
 * reads it and the reading fills the empty dates and the form type (decisions
 * 0281, 0676). Save records the row against that file; Cancel after an upload
 * trashes it.
 *
 * 🔒 Edit opens the same modal keyed to the row (decision 0676): the file
 * stands as it is and shows beside the form, and Save corrects the row in
 * place. A different document is a renewal, which is a new certificate.
 */
export function CertificateModal({ open, siteId, apiBaseUrl = '', onCancel, onSaved, editing = null }: SiteModalProps & { editing?: CertificateEdit | null }) {
  const initialReason = editing ? reasonCodeOf(editing.exemptionReason) : { code: '', other: '' };
  const [stateCode, setStateCode] = useState(editing?.stateCode ?? '');
  const [reasonCode, setReasonCode] = useState(initialReason.code);
  const [reasonOther, setReasonOther] = useState(initialReason.other);
  const [municipality, setMunicipality] = useState(editing?.municipality ?? '');
  const [municipalityOther, setMunicipalityOther] = useState('');
  const [formType, setFormType] = useState(editing?.formType ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(editing?.effectiveFrom ?? '');
  const [expiresOn, setExpiresOn] = useState(editing?.expiresOn ?? '');
  const [fileName, setFileName] = useState<string | null>(editing?.fileName ?? null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; kind: 'image' | 'pdf' | 'other' } | null>(null);
  const [read, setRead] = useState<ReadState>('idle');
  /** What the reading found beyond the fields it filled — the purchaser, blanket or single — shown beside the reading line (decision 0347). */
  const [found, setFound] = useState<CertificateReading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  /** The standing file of the row being edited, drawn from the one fetch-and-blob path (decision 0549). */
  const standing = useFetchedBlob(open && editing ? `${apiBaseUrl}${FILES_PATH}/${editing.fileId}/stream` : null);
  const standingPreview = standing.kind === 'drawn' ? { url: standing.url, kind: standing.contentType.startsWith('image/') ? 'image' as const : standing.contentType === 'application/pdf' ? 'pdf' as const : 'other' as const } : null;
  const shown = editing ? standingPreview : preview;
  /** The localities the system knows in the chosen state; a certificate for a home-rule city picks one, statewide picks none (decision 0306). */
  const municipalities = useRows<{ name: string }>(open && stateCode ? `${PAGES_PATH}/tax/municipalities?state=${stateCode}` : null, apiBaseUrl);
  const loaded = municipalities.state.kind === 'ready' ? municipalities.state.data.map((row) => row.name) : [];
  // The row's own municipality stays a choice while it is edited, whether or not a location names it (decision 0676).
  const known = editing?.municipality && !loaded.includes(editing.municipality) && editing.stateCode === stateCode ? [editing.municipality, ...loaded] : loaded;
  const reasons = stateCode ? exemptionReasonsFor(stateCode) : EXEMPTION_REASONS;
  const stateForm = stateCode ? stateExemptionForm(stateCode) : null;
  /** The library's hidden list, read once the modal opens; a failed read hides nothing (decision 0677). */
  const library = useApi<TaxFormsAnswer>(open ? TAX_FORMS_PATH : null, apiBaseUrl);
  const hiddenForms = new Set(library.state.kind === 'ready' ? (library.state.data.groups ?? []).flatMap((group) => group.forms).filter((form) => form.hidden).map((form) => form.file) : []);
  const stateCopies = (stateForm?.committed ?? []).filter((form) => !hiddenForms.has(form.file));
  const streamlinedCopy = (STREAMLINED_EXEMPTION_FORM.committed ?? []).find((form) => !hiddenForms.has(form.file)) ?? null;
  /** The forms the picked state offers and the two multistate ones, plus the row's own where the table no longer carries it (decision 0676). */
  const formTypes = exemptionFormTypesFor(stateCode);
  const formOptions = [...formTypes.map((row) => ({ value: row.code, label: line(row.code, '·', row.title) })), ...(formType && !formTypes.some((row) => row.code === formType) ? [{ value: formType, label: formType }] : [])];
  const trash = (id: string) => void post(`${FILES_PATH}/${id}/trash`, {}, apiBaseUrl);
  const showPreview = (file: File) => {
    // The preview is the browser's own object URL; a test runtime without one shows the kind and no picture.
    if (preview && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(preview.url);
    const kind = file.type.startsWith('image/') ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'other';
    setPreview({ url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '', kind });
  };
  const pick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    setError(null);
    setFileName(file.name);
    showPreview(file);
    setRead('reading');
    // A second pick replaces the first upload, which is trashed rather than left on the site.
    if (fileId) trash(fileId);
    setFileId(null);
    const form = new FormData();
    form.append('file', file, file.name);
    const result = await upload<{ fileId: string; check: CertificateReading }>(`${PAGES_PATH}/sites/${siteId}/certificates/file`, form, apiBaseUrl);
    if (!result.ok) {
      setFileName(null);
      setRead('idle');
      setFound(null);
      return setError(result.message);
    }
    const { check } = result.data;
    setFileId(result.data.fileId);
    // 🔒 A picture read with no Vision key held says so by name, and where the key is held (decision 0678).
    setRead(check.reading === 'unread' ? (check.unreadReason === 'noVisionKey' ? 'noVisionKey' : 'unread') : check.nameFound === false ? 'missing' : 'found');
    setFound(check.reading === 'read' ? check : null);
    // The reading fills only a field still empty (decision 0281); the state, the reason and the form it names are offered the same way (decisions 0306, 0676).
    if (check.effectiveDate) setEffectiveFrom((was) => was || check.effectiveDate!);
    if (check.expiryDate) setExpiresOn((was) => was || check.expiryDate!);
    if (check.stateCode) setStateCode((was) => was || check.stateCode!);
    if (check.exemptionReason) setReasonCode((was) => was || check.exemptionReason!);
    const readForm = exemptionFormType(check.formCode ?? null);
    if (readForm) setFormType((was) => was || readForm);
  };
  const reason = reasonCode === 'Other' ? reasonOther.trim() : reasonCode ? exemptionReasonLabel(reasonCode) : '';
  const chosenMunicipality = municipality === OTHER ? municipalityOther.trim() : municipality;
  const save = async () => {
    setError(null);
    if (!editing && !fileId) return setError(t('certificate.pickFile'));
    if (!stateCode) return setError(t('certificate.pickState'));
    if (!reason) return setError(t('certificate.pickReason'));
    const facts = { stateCode, exemptionReason: reason, formType: formType || null, municipality: chosenMunicipality || null, effectiveFrom, expiresOn: expiresOn || null };
    const result = editing
      ? await patch(`${PAGES_PATH}/sites/${siteId}/certificates/${editing.id}`, facts, apiBaseUrl)
      : await post(`${PAGES_PATH}/sites/${siteId}/certificates`, { fileId, ...facts }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    setFileId(null);
    onSaved();
    onCancel();
  };
  const cancel = () => {
    if (fileId) trash(fileId);
    setFileId(null);
    onCancel();
  };
  const readingKey: Record<Exclude<ReadState, 'idle'>, 'certificate.reading' | 'certificate.nameFound' | 'certificate.nameNotFound' | 'certificate.unread' | 'certificate.noVisionKey'> = { reading: 'certificate.reading', found: 'certificate.nameFound', missing: 'certificate.nameNotFound', unread: 'certificate.unread', noVisionKey: 'certificate.noVisionKey' };
  // The facts the form mapped (decision 0347): the purchaser it names and whether it is a blanket certificate, each only where read; the form itself fills Form type.
  const methodKey = found?.method === 'fields' ? 'certificate.readFields' : found?.method === 'ocr' ? 'certificate.readPicture' : found?.method === 'text' ? 'certificate.readText' : null;
  const facts = found ? [found.purchaserName ? line(t('certificate.purchaser'), found.purchaserName) : null, found.isBlanket === true ? t('certificate.blanket') : found.isBlanket === false ? t('certificate.singlePurchase') : null].filter((fact): fact is string => !!fact) : [];
  return (
    <Modal open={open} title={t(editing ? (editing.awaitingReview ? 'certificate.reviewTitle' : 'certificate.editTitle') : 'certificate.title')} onCancel={cancel} dataModal="certificate" width="wide" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: cancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {editing?.awaitingReview ? <p className={styles.hint} data-certificate-review="true">{t('certificate.reviewHint')}</p> : null}
      <div className={modalStyles.twoPanelStack} data-certificate-layout="form-and-preview" data-certificate-mode={editing ? 'edit' : 'add'}>
        <div>
          <div className={styles.field} data-certificate-file={editing ? 'standing' : fileId ? 'uploaded' : 'none'}>
            <span className={styles.fieldLabel}>{t('certificate.file')}</span>
            {editing ? (
              <>
                <span className={styles.hint} data-certificate-file-name="true">{fileName ?? t('certificate.noFile')}</span>
                <p className={styles.hint}>{t('certificate.fileStands')}</p>
              </>
            ) : (
              <div className={styles.actions}>
                <Button size={Size.Small} color={Color.Navy} text={[t('certificate.chooseFile')]} actions={[() => input.current?.click()]} />
                <span className={styles.hint} data-certificate-file-name="true">{fileName ?? t('certificate.noFile')}</span>
              </div>
            )}
            {editing ? null : <input ref={input} className={styles.hiddenInput} type="file" accept="application/pdf,image/*" aria-label={t('certificate.file')} onChange={(event) => void pick(event)} data-certificate-input="true" />}
          </div>
          {read !== 'idle' ? <p className={styles.hint} data-certificate-reading={read}>{t(readingKey[read])}</p> : null}
          {methodKey && facts.length > 0 ? <p className={styles.hint} data-certificate-read-by={found?.method ?? ''}>{line(t(methodKey), facts.join(' · '))}</p> : null}
          <Select size={Size.Large} label={t('certificate.state')} value={stateCode} onChange={(chosen) => { setStateCode(chosen); setMunicipality(''); }} required placeholder={t('certificate.pickState')} options={US_REGIONS.map((code) => ({ value: code, label: line(code, '·', US_STATE_NAMES[code]) }))} dataAttribute={{ 'data-certificate-state': 'true' }} />
          {/* Where the blank form for the picked state lives, so the form is one click from where the certificate is recorded (product owner, 2026-09-12). */}
          {stateCode ? (
            stateForm ? (
              <p className={styles.hint} data-certificate-form={stateCode}>
                {t('certificate.getForm')}{' '}
                {/* 🔒 The library's own copy of each of the state's forms not hidden, and the revenue site where none is (decision 0677). */}
                {stateCopies.length > 0
                  ? stateCopies.map((form, index) => (
                    <span key={form.file}>
                      {index > 0 ? ', ' : null}
                      <a href={`${apiBaseUrl}${taxFormPath(form.file)}`} target="_blank" rel="noopener noreferrer" data-certificate-form-link="state" data-certificate-form-copy={form.file}>{form.title}</a>
                    </span>
                  ))
                  : <a href={stateForm.url} target="_blank" rel="noopener noreferrer" data-certificate-form-link="state">{stateForm.form}</a>}
                {' '}{t('certificate.getStreamlined')}{' '}
                {streamlinedCopy
                  ? <a href={`${apiBaseUrl}${taxFormPath(streamlinedCopy.file)}`} target="_blank" rel="noopener noreferrer" data-certificate-form-link="streamlined" data-certificate-form-copy={streamlinedCopy.file}>{STREAMLINED_EXEMPTION_FORM.form}</a>
                  : <a href={STREAMLINED_EXEMPTION_FORM.url} target="_blank" rel="noopener noreferrer" data-certificate-form-link="streamlined">{STREAMLINED_EXEMPTION_FORM.form}</a>}
                {' '}{t('certificate.getLibrary')}{' '}
                <a href={TAX_FORMS_ROUTE} target="_blank" rel="noopener noreferrer" data-certificate-form-link="library">{t('certificate.library')}</a>
              </p>
            ) : (
              <p className={styles.hint} data-certificate-form="none">{t('certificate.noForm')}</p>
            )
          ) : null}
          <Select size={Size.Large} label={t('certificate.municipality')} value={municipality} onChange={setMunicipality} disabled={!stateCode} placeholder={t('certificate.statewide')} options={[...known.map((name) => ({ value: name, label: name })), { value: OTHER, label: t('certificate.otherMunicipality') }]} dataAttribute={{ 'data-certificate-municipality': 'true' }} />
          {municipality === OTHER ? <TextInput size={Size.Medium} label={t('certificate.municipalityName')} value={municipalityOther} onChange={setMunicipalityOther} name="municipalityOther" /> : null}
          <Select size={Size.Large} label={t('certificate.reason')} value={reasonCode} onChange={setReasonCode} required placeholder={t('certificate.pickReason')} options={reasons.map((row) => ({ value: row.code, label: row.label }))} dataAttribute={{ 'data-certificate-reason': 'true' }} />
          {reasonCode === 'Other' ? <TextInput size={Size.Medium} label={t('certificate.reasonOther')} value={reasonOther} onChange={setReasonOther} name="exemptionReasonOther" /> : null}
          <Select size={Size.Large} label={t('certificate.formType')} value={formType} onChange={setFormType} placeholder={t('certificate.pickFormType')} options={formOptions} dataAttribute={{ 'data-certificate-form-type': 'true' }} />
          <DatePicker size={Size.Small} label={t('certificate.effectiveFrom')} value={effectiveFrom} onChange={setEffectiveFrom} mode="date" required name="effectiveFrom" />
          <DatePicker size={Size.Small} label={t('certificate.expiresOn')} value={expiresOn} onChange={setExpiresOn} mode="date" name="expiresOn" />
        </div>
        <div className={styles.certificatePreview} data-certificate-preview={shown ? shown.kind : 'none'}>
          <span className={styles.fieldLabel}>{t('certificate.preview')}</span>
          {shown?.kind === 'image' ? <img className={styles.certificateImage} src={shown.url} alt={fileName ?? ''} /> : null}
          {shown?.kind === 'pdf' ? <iframe className={styles.certificateFrame} src={shown.url} title={fileName ?? t('certificate.preview')} /> : null}
          {shown?.kind === 'other' ? <p className={styles.hint}>{t('certificate.noPreview')}</p> : null}
          {!shown ? <p className={styles.hint}>{t('certificate.previewHint')}</p> : null}
        </div>
      </div>
    </Modal>
  );
}

/**
 * End on a live certificate row (decision 0676). 🔨 It is asked inside a
 * dialog, never on one press of the row (decision 0589), because from the
 * moment it is pressed the certificate exempts nothing.
 */
export function CertificateEndModal({ open, siteId, row, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps & { row: { id: string; label: string; value: string } | null }) {
  const [error, setError] = useState<string | null>(null);
  const end = async () => {
    setError(null);
    if (!row) return;
    const result = await post(`${PAGES_PATH}/sites/${siteId}/certificates/${row.id}/end`, {}, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('certificate.endTitle')} onCancel={onCancel} dataModal="certificate-end" actions={[{ kind: 'primary', label: t('certificate.end'), onAct: () => void end() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {row ? <p className={styles.hint} data-certificate-ending={row.id}>{row.value ? line(row.label, '·', row.value) : row.label}</p> : null}
      <p className={styles.hint}>{t('certificate.endConsequence')}</p>
    </Modal>
  );
}

/**
 * 🔒 Remove (decision 7891; T3181): asked inside a dialog, as End is, for a certificate placed on the wrong site that no
 * document names. The row is deleted and its file stays on the company's Files; a refusal names the document it exempted.
 */
export function CertificateRemoveModal({ open, siteId, row, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps & { row: { id: string; label: string; value: string } | null }) {
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setError(null);
    if (!row) return;
    const result = await post(`${PAGES_PATH}/sites/${siteId}/certificates/${row.id}/remove`, {}, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('certificate.removeTitle')} onCancel={onCancel} dataModal="certificate-remove" actions={[{ kind: 'primary', label: t('certificate.remove'), onAct: () => void remove() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {row ? <p className={styles.hint} data-certificate-removing={row.id}>{row.value ? line(row.label, '·', row.value) : row.label}</p> : null}
      <p className={styles.hint}>{t('certificate.removeConsequence')}</p>
    </Modal>
  );
}

/** 🔒 Move (decision 7891; T3181): the certificate and its file to another open site of the same company, picked from a list. */
export function CertificateMoveModal({ open, siteId, row, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps & { row: { id: string; label: string; value: string } | null }) {
  const [toSiteId, setToSiteId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const sites = useRows<{ id: string; label: string }>(open && row ? `${PAGES_PATH}/sites/${siteId}/certificate-move-sites` : null, apiBaseUrl);
  const choices = sites.state.kind === 'ready' ? sites.state.data : [];
  const move = async () => {
    setError(null);
    if (!row || !toSiteId) return;
    const result = await post(`${PAGES_PATH}/sites/${siteId}/certificates/${row.id}/move`, { toSiteId }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('certificate.moveTitle')} onCancel={onCancel} dataModal="certificate-move" actions={[{ kind: 'primary', label: t('certificate.move'), onAct: () => void move(), disabled: !toSiteId, disabledReason: t('certificate.movePick') }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {row ? <p className={styles.hint} data-certificate-moving={row.id}>{row.value ? line(row.label, '·', row.value) : row.label}</p> : null}
      {sites.state.kind === 'ready' && choices.length === 0 ? (
        <p className={styles.hint} data-certificate-move-none="true">{t('certificate.moveNone')}</p>
      ) : (
        <Select size={Size.Medium} label={t('certificate.moveSite')} value={toSiteId} onChange={setToSiteId} placeholder={t('edit.none')} options={choices.map((site) => ({ value: site.id, label: site.label }))} dataAttribute={{ 'data-certificate-move-site': 'true' }} />
      )}
      <p className={styles.hint}>{t('certificate.moveConsequence')}</p>
    </Modal>
  );
}

/** A requirement on the site from the shared type list, dated from today unless told otherwise (piece 10; decision 0274). */
/**
 * A requirement row ends on a date and is never edited in place; a changed requirement is a new row after it (decision 0295).
 * 🔒 Decision 10501 (T3577): a row the server marks removable opens this window as Remove, which deletes the row and
 * leaves no ended one; the server asks its rule again and refuses a row that fails it.
 */
export function SiteRequirementEndModal({ open, siteId, row, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps & { row: { id: string; label: string; value: string; removable?: boolean } | null }) {
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const removes = row?.removable === true;
  const save = async () => {
    setError(null);
    if (!row) return;
    const path = `${PAGES_PATH}/sites/${siteId}/requirements/${row.id}`;
    const result = removes ? await del(path, apiBaseUrl) : await patch(path, { endDate: endDate || null }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={removes ? t('requirement.removeTitle') : t('requirement.endTitle')} onCancel={onCancel} dataModal={removes ? 'site-requirement-remove' : 'site-requirement-end'} actions={[{ kind: 'primary', label: removes ? t('requirement.remove') : t('requirement.end'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {row ? <p className={styles.hint} data-requirement-ending={row.id}>{line(row.label, '·', row.value)}</p> : null}
      {removes
        ? <p className={styles.hint} data-requirement-removing={row.id}>{t('requirement.removeConsequence')}</p>
        : <DatePicker size={Size.Small} label={t('requirement.endDate')} value={endDate} onChange={setEndDate} mode="date" name="endDate" />}
    </Modal>
  );
}

const SITE_ROLES = [
  { value: 'BillTo', label: 'siteRole.billTo' },
  { value: 'ShipTo', label: 'siteRole.shipTo' },
  { value: 'Service', label: 'siteRole.service' },
] as const;

/** + Add role on the Roles tile: one dated SiteRole row from today unless told otherwise (decision 0077); two open rows for one role are refused by the server. */
export function SiteRoleModal({ open, siteId, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps) {
  const [role, setRole] = useState('');
  const [startDate, setStartDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    if (!role) return setError(t('siteRole.pickRole'));
    const result = await post(`${PAGES_PATH}/sites/${siteId}/roles`, { role, startDate: startDate || null }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('siteRole.title')} onCancel={onCancel} dataModal="site-role" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Select size={Size.Medium} label={t('siteRole.role')} value={role} onChange={setRole} placeholder={t('siteRole.pickRole')} options={SITE_ROLES.map((row) => ({ value: row.value, label: t(row.label) }))} dataAttribute={{ 'data-site-role': 'true' }} />
      <DatePicker size={Size.Small} label={t('siteRole.startDate')} value={startDate} onChange={setStartDate} mode="date" name="startDate" />
    </Modal>
  );
}

/** A row adds to the Company default, overrides it for the same type, or, ticked Not required at this site, waives it (decision 0336). */
export function SiteRequirementModal({ open, siteId, apiBaseUrl = '', onCancel, onSaved }: SiteModalProps) {
  const types = useRows<{ id: string; name: string; kind: string }>(open ? `${PAGES_PATH}/site-requirement-types` : null, apiBaseUrl);
  const [typeId, setTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [waived, setWaived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    if (!typeId) return setError(t('requirement.pickType'));
    const result = await post(`${PAGES_PATH}/sites/${siteId}/requirements`, { siteRequirementTypeId: typeId, startDate: startDate || null, required: !waived }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('requirement.title')} onCancel={onCancel} dataModal="site-requirement" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Select size={Size.Large} label={t('requirement.type')} value={typeId} onChange={setTypeId} placeholder={t('requirement.pickType')} options={(types.state.kind === 'ready' ? types.state.data : []).map((row) => ({ value: row.id, label: line(row.name, '·', row.kind) }))} dataAttribute={{ 'data-requirement-type': 'true' }} />
      <DatePicker size={Size.Small} label={t('requirement.startDate')} value={startDate} onChange={setStartDate} mode="date" name="startDate" />
      <div data-requirement-waive={waived ? 'true' : 'false'}>
        <Checkbox label={t('requirement.notRequiredHere')} checked={waived} onChange={setWaived} name="notRequiredHere" />
        <p className={styles.hint}>{t('requirement.notRequiredHint')}</p>
      </div>
    </Modal>
  );
}
